import React, { useEffect, useMemo, useState, useCallback } from "react";
import {
  MapContainer,
  TileLayer,
  Marker,
  Popup,
  GeoJSON,
  Tooltip,
  useMap,
  useMapEvents,
  Pane,
} from "react-leaflet";
import MarkerClusterGroup from "react-leaflet-cluster";
import "leaflet/dist/leaflet.css";
import "leaflet.markercluster/dist/MarkerCluster.css";
import "leaflet.markercluster/dist/MarkerCluster.Default.css";
import L from "leaflet";

// Icône par défaut Leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl:
    "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

const DEPARTMENT_GROUPING_MAX_ZOOM = 7;

function toLatLng(club) {
  if (!club?.coordonnees_gps) return null;

  const [latStr, lonStr] = String(club.coordonnees_gps)
    .split(",")
    .map((v) => v.trim());

  const lat = parseFloat(latStr);
  const lon = parseFloat(lonStr);

  if (Number.isFinite(lat) && Number.isFinite(lon)) {
    return [lat, lon];
  }

  return null;
}

function boundsFromClubs(clubs) {
  const pts = clubs.map(toLatLng).filter(Boolean);
  if (!pts.length) return null;
  return L.latLngBounds(pts);
}

function FitBounds({ bounds }) {
  const map = useMap();

  useEffect(() => {
    if (!bounds || !bounds.isValid()) return;
    map.fitBounds(bounds, { padding: [24, 24] });
  }, [bounds, map]);

  return null;
}

function MapZoomWatcher({ onZoomChange }) {
  const map = useMapEvents({
    zoomend: () => {
      onZoomChange(map.getZoom());
    },
  });

  useEffect(() => {
    onZoomChange(map.getZoom());
  }, [map, onZoomChange]);

  return null;
}

function normalizeWebsiteUrl(url) {
  if (!url) return null;
  const trimmed = String(url).trim();
  if (!trimmed) return null;
  return /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
}

function normalizeImageUrl(url) {
  if (!url) return null;
  const trimmed = String(url).trim();
  if (!trimmed) return null;

  if (/^https?:\/\//i.test(trimmed) || trimmed.startsWith("//")) {
    return trimmed;
  }

  if (trimmed.startsWith("/")) {
    return `${window.location.origin}${trimmed}`;
  }

  return `${window.location.origin}/${trimmed}`;
}

function createClusterCustomIcon(cluster) {
  const count = cluster.getChildCount();

  let size = 40;
  let fontSize = 13;

  if (count >= 20) {
    size = 48;
    fontSize = 14;
  }
  if (count >= 50) {
    size = 56;
    fontSize = 15;
  }

  return L.divIcon({
    html: `
      <div style="
        width:${size}px;
        height:${size}px;
        border-radius:9999px;
        display:flex;
        align-items:center;
        justify-content:center;
        background: radial-gradient(circle at 30% 30%, #60a5fa 0%, #2563eb 70%);
        border: 4px solid rgba(255,255,255,0.95);
        box-shadow: 0 8px 20px rgba(37,99,235,0.35);
        color:white;
        font-weight:800;
        font-size:${fontSize}px;
      ">
        ${count}
      </div>
    `,
    className: "custom-marker-cluster",
    iconSize: L.point(size, size, true),
  });
}

function createDepartmentAggregateIcon({ code, count, isSelected }) {
  let size = 62;
  let countFontSize = 18;

  if (count >= 20) {
    size = 70;
    countFontSize = 19;
  }
  if (count >= 50) {
    size = 78;
    countFontSize = 20;
  }

  const background = isSelected
    ? "linear-gradient(135deg, #4f46e5 0%, #312e81 100%)"
    : "linear-gradient(135deg, #0f172a 0%, #1d4ed8 100%)";

  return L.divIcon({
    html: `
      <div style="
        width:${size}px;
        height:${size}px;
        border-radius:22px;
        display:flex;
        flex-direction:column;
        align-items:center;
        justify-content:center;
        gap:2px;
        background:${background};
        border: 4px solid rgba(255,255,255,0.95);
        box-shadow: 0 10px 24px rgba(15,23,42,0.28);
        color:white;
        font-weight:800;
      ">
        <div style="
          font-size:10px;
          line-height:1;
          letter-spacing:0.08em;
          opacity:0.82;
          text-transform:uppercase;
        ">
          DEP ${code}
        </div>
        <div style="
          font-size:${countFontSize}px;
          line-height:1;
        ">
          ${count}
        </div>
      </div>
    `,
    className: "custom-department-aggregate",
    iconSize: L.point(size, size, true),
  });
}

function InfoRow({ icon, children }) {
  return (
    <div className="flex items-start gap-2 text-[13px] text-slate-700">
      <span className="mt-[1px] shrink-0">{icon}</span>
      <div className="min-w-0 break-words">{children}</div>
    </div>
  );
}

function ClubPopupCard({ club, latlng }) {
  const [imgError, setImgError] = useState(false);

  const websiteUrl = normalizeWebsiteUrl(club.site_club);
  const logoUrl = !imgError ? normalizeImageUrl(club.logo_url) : null;
  const googleMapsUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
    latlng.join(",")
  )}`;

  return (
    <div className="font-satoshi w-[280px] overflow-hidden rounded-2xl bg-white text-slate-900">
      <div className="bg-gradient-to-r from-blue-700 via-indigo-600 to-sky-500 px-4 py-4 text-white">
        <div className="flex items-start gap-3">
          <div className="h-14 w-14 shrink-0 overflow-hidden rounded-2xl bg-white/15 ring-1 ring-white/25 backdrop-blur-sm flex items-center justify-center">
            {logoUrl ? (
              <img
                src={logoUrl}
                alt={club.nom_club || "Logo du club"}
                className="h-full w-full object-cover"
                onError={() => setImgError(true)}
              />
            ) : (
              <span className="text-lg font-bold uppercase text-white">
                {String(club?.nom_club || "C").charAt(0)}
              </span>
            )}
          </div>

          <div className="min-w-0">
            <p className="text-[11px] uppercase tracking-[0.18em] text-white/75">
              Club de judo
            </p>
            <h3 className="mt-1 text-sm font-bold leading-tight">
              {club.nom_club || "Club sans nom"}
            </h3>

            {club.departement_club && (
              <div className="mt-2 inline-flex items-center rounded-full bg-white/15 px-2.5 py-1 text-[11px] font-semibold text-white ring-1 ring-white/20">
                Département {club.departement_club}
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="space-y-3 px-4 py-4">
        {club.adresse_club && (
          <InfoRow icon="📍">{club.adresse_club}</InfoRow>
        )}

        {club.tel_club && (
          <InfoRow icon="📞">
            <a
              href={`tel:${club.tel_club}`}
              className="hover:text-blue-700 hover:underline"
            >
              {club.tel_club}
            </a>
          </InfoRow>
        )}

        {club.email_club && (
          <InfoRow icon="✉️">
            <a
              href={`mailto:${club.email_club}`}
              className="text-blue-700 hover:underline"
            >
              {club.email_club}
            </a>
          </InfoRow>
        )}

        {websiteUrl && (
          <InfoRow icon="🌐">
            <a
              href={websiteUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-indigo-700 hover:underline"
            >
              Site internet du club
            </a>
          </InfoRow>
        )}

        <div className="grid grid-cols-1 gap-2 pt-1">
          <a
            href={googleMapsUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center rounded-xl bg-slate-900 px-3 py-2 text-xs font-semibold text-white transition hover:bg-slate-800 hover:text-white"
          >
            Ouvrir dans Google Maps
          </a>

          {websiteUrl && (
            <a
              href={websiteUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-xs font-semibold text-slate-700 transition hover:bg-slate-100"
            >
              Visiter le site du club
            </a>
          )}
        </div>
      </div>
    </div>
  );
}

export default function CarteClubsNouvelleAquitaine({
  clubs = [],
  filterDepartement = "",
  onDepartmentSelect,
  geoJsonUrl = "/departements-nouvelle-aquitaine.geojson",
}) {
  const [departementsGeoJson, setDepartementsGeoJson] = useState([]);
  const [targetBounds, setTargetBounds] = useState(null);
  const [currentZoom, setCurrentZoom] = useState(7);

  useEffect(() => {
    (async () => {
      try {
        const resp = await fetch(geoJsonUrl);
        const data = await resp.json();
        setDepartementsGeoJson(
          Array.isArray(data?.features) ? data.features : []
        );
      } catch (e) {
        console.error("Erreur chargement GeoJSON :", e);
        setDepartementsGeoJson([]);
      }
    })();
  }, [geoJsonUrl]);

  const visibleClubs = useMemo(() => {
    if (!filterDepartement) return clubs;
    return clubs.filter(
      (club) => String(club?.departement_club) === String(filterDepartement)
    );
  }, [clubs, filterDepartement]);

  const clubsWithCoords = useMemo(() => {
    return visibleClubs
      .map((club) => {
        const latlng = toLatLng(club);
        return latlng ? { club, latlng } : null;
      })
      .filter(Boolean);
  }, [visibleClubs]);

  const clubsWithoutCoords = visibleClubs.length - clubsWithCoords.length;

  const depsWithClubs = useMemo(() => {
    return [
      ...new Set(visibleClubs.map((c) => c.departement_club).filter(Boolean)),
    ].map(String);
  }, [visibleClubs]);

  const departmentMetaByCode = useMemo(() => {
    const map = new Map();

    departementsGeoJson.forEach((feature) => {
      const code = String(feature?.properties?.code || "");
      const nom = feature?.properties?.nom || "";
      if (!code) return;

      let center = null;
      try {
        const bounds = L.geoJSON(feature).getBounds();
        if (bounds?.isValid()) {
          const c = bounds.getCenter();
          center = [c.lat, c.lng];
        }
      } catch (e) {
        center = null;
      }

      map.set(code, { code, nom, center });
    });

    return map;
  }, [departementsGeoJson]);

  const departmentAggregates = useMemo(() => {
    const acc = new Map();

    clubsWithCoords.forEach(({ club, latlng }) => {
      const code = String(club?.departement_club || "");
      if (!code) return;

      if (!acc.has(code)) {
        const meta = departmentMetaByCode.get(code);
        acc.set(code, {
          code,
          nom: meta?.nom || `Département ${code}`,
          count: 0,
          latSum: 0,
          lngSum: 0,
          center: meta?.center || null,
        });
      }

      const row = acc.get(code);
      row.count += 1;
      row.latSum += latlng[0];
      row.lngSum += latlng[1];
    });

    return Array.from(acc.values())
      .map((row) => ({
        ...row,
        latlng:
          row.center ||
          (row.count > 0
            ? [row.latSum / row.count, row.lngSum / row.count]
            : null),
      }))
      .filter((row) => Array.isArray(row.latlng));
  }, [clubsWithCoords, departmentMetaByCode]);

  const styleDepartement = useCallback(
    (feature) => {
      const code = String(feature?.properties?.code || "");
      const highlighted = depsWithClubs.includes(code);
      const isSelected =
        filterDepartement && String(code) === String(filterDepartement);

      return {
        color: isSelected ? "#4338ca" : "#0f172a",
        weight: isSelected ? 3 : 1,
        fillColor: isSelected
          ? "#6366f1"
          : highlighted
          ? "#93c5fd"
          : "#d1d5db",
        fillOpacity: isSelected ? 0.35 : highlighted ? 0.22 : 0.12,
      };
    },
    [depsWithClubs, filterDepartement]
  );

  useEffect(() => {
    if (filterDepartement && departementsGeoJson.length) {
      const feat = departementsGeoJson.find(
        (f) => String(f?.properties?.code) === String(filterDepartement)
      );

      if (feat) {
        const b = L.geoJSON(feat).getBounds();
        setTargetBounds(b);
        return;
      }
    }

    const b = boundsFromClubs(visibleClubs);
    setTargetBounds(b);
  }, [filterDepartement, departementsGeoJson, visibleClubs]);

  const onEachFeature = (feature, layer) => {
    const code = feature?.properties?.code;
    const nom = feature?.properties?.nom;

    layer.bindTooltip(`${code} — ${nom}`, {
      sticky: true,
      direction: "top",
    });

    layer.on("click", () => {
      onDepartmentSelect?.(code);
    });

    layer.on("mouseover", () => {
      layer.setStyle({
        ...styleDepartement(feature),
        weight: 2,
        fillOpacity: 0.35,
      });
    });

    layer.on("mouseout", () => {
      layer.setStyle(styleDepartement(feature));
    });
  };

  const showDepartmentAggregates =
    !filterDepartement && currentZoom <= DEPARTMENT_GROUPING_MAX_ZOOM;

  return (
    <div className="rounded-xl ring-1 ring-slate-200 overflow-hidden bg-white">
      <div className="px-4 py-3 flex flex-wrap items-center gap-2 text-sm">
        <span className="inline-flex items-center px-2.5 py-1 rounded-full bg-slate-100">
          {visibleClubs.length} club{visibleClubs.length > 1 ? "s" : ""}
        </span>

        <span className="inline-flex items-center px-2.5 py-1 rounded-full bg-emerald-100 text-emerald-800">
          {clubsWithCoords.length} géolocalisé
          {clubsWithCoords.length > 1 ? "s" : ""}
        </span>

        {clubsWithoutCoords > 0 && (
          <span className="inline-flex items-center px-2.5 py-1 rounded-full bg-amber-100 text-amber-800">
            {clubsWithoutCoords} sans coordonnées
          </span>
        )}

        {showDepartmentAggregates && (
          <span className="inline-flex items-center px-2.5 py-1 rounded-full bg-indigo-100 text-indigo-800">
            Vue agrégée par département
          </span>
        )}

        <span className="ml-auto opacity-70">
          Cliquez sur un département pour filtrer, puis zoomez pour séparer les
          clubs.
        </span>
      </div>

      <div className="relative" style={{ height: 600 }}>
        <MapContainer
          center={[45.7, 0.6]}
          zoom={7}
          minZoom={6}
          scrollWheelZoom
          preferCanvas={true}
          style={{ height: "100%", width: "100%" }}
          className="z-0"
        >
          <MapZoomWatcher onZoomChange={setCurrentZoom} />

          <TileLayer
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            attribution="&copy; OpenStreetMap contributors"
          />

          <Pane name="departements" style={{ zIndex: 400 }}>
            {departementsGeoJson.length > 0 && (
              <GeoJSON
                pane="departements"
                data={{
                  type: "FeatureCollection",
                  features: departementsGeoJson,
                }}
                style={styleDepartement}
                onEachFeature={onEachFeature}
              />
            )}
          </Pane>

          {showDepartmentAggregates ? (
            <>
              {departmentAggregates.map((dep) => (
                <Marker
                  key={`dep-${dep.code}`}
                  position={dep.latlng}
                  icon={createDepartmentAggregateIcon({
                    code: dep.code,
                    count: dep.count,
                    isSelected:
                      String(filterDepartement || "") === String(dep.code),
                  })}
                  eventHandlers={{
                    click: () => onDepartmentSelect?.(dep.code),
                  }}
                >
                  <Tooltip direction="top" offset={[0, -10]}>
                    {dep.code} — {dep.nom} • {dep.count} club
                    {dep.count > 1 ? "s" : ""}
                  </Tooltip>

                  <Popup maxWidth={260}>
                    <div className="min-w-[200px] space-y-2">
                      <div className="font-semibold text-slate-900">
                        {dep.code} — {dep.nom}
                      </div>
                      <div className="text-sm text-slate-600">
                        {dep.count} club{dep.count > 1 ? "s" : ""} géolocalisé
                        {dep.count > 1 ? "s" : ""}
                      </div>
                      <button
                        type="button"
                        onClick={() => onDepartmentSelect?.(dep.code)}
                        className="inline-flex items-center justify-center rounded-xl bg-slate-900 px-3 py-2 text-xs font-semibold text-white transition hover:bg-slate-800"
                      >
                        Voir ce département
                      </button>
                    </div>
                  </Popup>
                </Marker>
              ))}
            </>
          ) : (
            <MarkerClusterGroup
              chunkedLoading
              showCoverageOnHover={false}
              spiderfyOnMaxZoom={true}
              zoomToBoundsOnClick={true}
              removeOutsideVisibleBounds={true}
              maxClusterRadius={60}
              disableClusteringAtZoom={11}
              iconCreateFunction={createClusterCustomIcon}
            >
              {clubsWithCoords.map(({ club, latlng }, idx) => (
                <Marker
                  key={`${club.id || idx}-${latlng[0]}-${latlng[1]}`}
                  position={latlng}
                >
                  <Tooltip direction="top" offset={[0, -10]}>
                    {club.nom_club}
                  </Tooltip>

                  <Popup maxWidth={320} className="club-popup-modern">
                    <ClubPopupCard club={club} latlng={latlng} />
                  </Popup>
                </Marker>
              ))}
            </MarkerClusterGroup>
          )}

          {targetBounds && <FitBounds bounds={targetBounds} />}
        </MapContainer>
      </div>
    </div>
  );
}